import React, { useRef, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { makeApiRequest, makeMultipartApiRequest } from '../../Shared/Api';

const LOGO_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

const LogoUpload = ({ organization, onUpdated, onError, onSuccess }) => {
	const inputRef = useRef(null);
	const [uploading, setUploading] = useState(false);
	const [removing, setRemoving] = useState(false);

	const handleChooseFile = () => {
		inputRef.current?.click();
	};

	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file) {
			return;
		}

		if (!LOGO_ACCEPT.split(',').includes(file.type)) {
			onError?.('Logo must be a JPG, PNG, WebP, or GIF image.');
			return;
		}
		if (file.size > LOGO_MAX_BYTES) {
			onError?.('Logo must be 2 MB or smaller.');
			return;
		}

		setUploading(true);
		try {
			const formData = new FormData();
			formData.append('logo', file);
			const response = await makeMultipartApiRequest(
				'PATCH',
				'/auth/my-organization/',
				formData,
			);
			onUpdated?.(response);
			onSuccess?.('Company logo uploaded.');
		} catch (error) {
			onError?.(error.message || 'Failed to upload logo.');
		} finally {
			setUploading(false);
		}
	};

	const handleRemove = async () => {
		setRemoving(true);
		try {
			const response = await makeApiRequest('PATCH', '/auth/my-organization/', {
				clear_logo: true,
			});
			onUpdated?.(response);
			onSuccess?.('Company logo removed.');
		} catch (error) {
			onError?.(error.message || 'Failed to remove logo.');
		} finally {
			setRemoving(false);
		}
	};

	const logoUrl = organization?.logo_url;
	const busy = uploading || removing;

	return (
		<Box>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={2}
				alignItems={{ xs: 'stretch', sm: 'flex-start' }}
			>
				<Box
					sx={{
						width: { xs: '100%', sm: 200 },
						height: 100,
						borderRadius: 2,
						border: 1,
						borderColor: 'divider',
						borderStyle: 'dashed',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						bgcolor: 'action.hover',
						overflow: 'hidden',
						flexShrink: 0,
					}}
				>
					{logoUrl ? (
						<Box
							component="img"
							src={logoUrl}
							alt={`${organization?.name || 'Company'} logo`}
							sx={{
								maxWidth: '100%',
								maxHeight: '100%',
								objectFit: 'contain',
								p: 1,
							}}
						/>
					) : (
						<Stack
							alignItems="center"
							spacing={0.5}
							sx={{ color: 'text.secondary', px: 2 }}
						>
							<ImageOutlinedIcon />
							<Typography variant="caption" align="center">
								No logo uploaded
							</Typography>
						</Stack>
					)}
				</Box>

				<Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ lineHeight: 1.6 }}
					>
						Your logo appears at the top of bid proposals, invoices, and
						customer emails. For best results, use a horizontal logo around{' '}
						<strong>400x200 px</strong> (2:1 ratio), PNG or JPG with a white or
						transparent background, at least 72 DPI, and under 2 MB.
					</Typography>

					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
						<input
							ref={inputRef}
							type="file"
							accept={LOGO_ACCEPT}
							hidden
							onChange={handleFileChange}
						/>
						<Button
							variant="outlined"
							onClick={handleChooseFile}
							disabled={busy}
							sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
						>
							{uploading ? (
								<>
									<CircularProgress size={18} sx={{ mr: 1 }} />
									Uploading...
								</>
							) : logoUrl ? (
								'Replace logo'
							) : (
								'Upload logo'
							)}
						</Button>
						{logoUrl ? (
							<Button
								variant="text"
								color="error"
								onClick={handleRemove}
								disabled={busy}
								sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
							>
								{removing ? 'Removing...' : 'Remove logo'}
							</Button>
						) : null}
					</Stack>
				</Stack>
			</Stack>

			{logoUrl ? (
				<Alert severity="info" sx={{ mt: 2 }}>
					This logo is included in outbound proposal and invoice emails.
				</Alert>
			) : null}
		</Box>
	);
};

export default LogoUpload;
